import React, { useState, useEffect } from 'react';
import { getDataWithFetch, postDataWithFetch } from './Utils';
import '../assets/css/checkbox.css';

const TimeZoneConfig = ({ data }) => {
	const [showTimeZones, setShowTimeZones] = useState(false);
	const [timeZones, setTimeZones] = useState([]);
	const [newCityName, setNewCityName] = useState('');
	const [loading, setLoading] = useState(false);
	const [statusMessage, setStatusMessage] = useState('');
	const [errorMessage, setErrorMessage] = useState('');

	// Load existing time zones from data or fetch from server
	useEffect(() => {
		if (data.BASE_URL) {
			// Initialize with default time zones if available

            (async() => {
                const url = data.BASE_URL + 'index.php/misc/get_time_zones';
                const res = await getDataWithFetch(url);
                // console.log('DEBUG:', res);

                if (res && Array.isArray(res)) {
                    setTimeZones(res);
                } else {
                    setTimeZones([
                        { label: "Local Time", timeZone: "" }
                    ]);
                }
            })();
		}
	}, [data.BASE_URL]);

	const toggleTimeZones = () => {
		setShowTimeZones(!showTimeZones);
		setStatusMessage('');
		setErrorMessage('');
	};

	const handleAddCity = async () => {
		if (!newCityName.trim()) {
			setErrorMessage('Please enter a city name');
			return;
		}

		setLoading(true);
		setErrorMessage('');
		setStatusMessage('');

		try {
			const url = `${data.BASE_URL}index.php/misc/geocode_address_and_parse/${encodeURIComponent(newCityName)}`;
			const response = await fetch(url);
			const result = await response.json();

			// console.log('Geocode result:', result);

			if (result.status && result.TIMEZONE) {
				// Check if city already exists
				const exists = timeZones.some(tz => 
					tz.label.toLowerCase() === result.CITY.toLowerCase()
				);

				if (exists) {
					setErrorMessage(`${result.CITY} already exists in the list`);
				} else {
					const newTimeZone = {
						label: result.CITY,
						timeZone: result.TIMEZONE,
						showOnTempPage: false
					};
					setTimeZones([...timeZones, newTimeZone]);
					setNewCityName('');
					setStatusMessage(`Successfully added ${result.CITY} (${result.TIMEZONE})`);
				}
			} else {
				setErrorMessage(`Unable to find timezone for ${newCityName}. ${result.TIMEZONE || 'City not found'}`);
			}
		} catch (error) {
			console.error('Error geocoding city:', error);
			setErrorMessage(`Error: ${error.message}`);
		} finally {
			setLoading(false);
		}
	};

	const handleRemoveCity = (index) => {
		// Prevent removing "Local Time" entry (index 0)
		if (index === 0) {
			setErrorMessage('Cannot remove "Local Time" entry');
			return;
		}

		const updatedTimeZones = timeZones.filter((_, i) => i !== index);
		setTimeZones(updatedTimeZones);
		setStatusMessage('City removed. Click SAVE to apply changes.');
	};

	const getTimezoneOffset = (timeZone) => {
		if (!timeZone) return -12 * 60; // "Local Time" sorts first
		try {
			const now = new Date();
			const tzDate = new Date(now.toLocaleString('en-US', { timeZone }));
			const localDate = new Date(now.toLocaleString('en-US', { timeZone: 'UTC' }));
			return (tzDate - localDate) / 60000; // offset in minutes
		} catch {
			return 0; // fallback for invalid timezones
		}
	};

	const handleSaveTimeZones = async () => {
		setLoading(true);
		setErrorMessage('');
		setStatusMessage('');

		try {
			// Sort time zones by UTC offset (west to east)
			const sortedTimeZones = [...timeZones].sort((a, b) => {
				const offsetA = getTimezoneOffset(a.timeZone);
				const offsetB = getTimezoneOffset(b.timeZone);
				return offsetA - offsetB;
			});

			const url = `${data.BASE_URL}index.php/misc/update_time_zones`;
			const params = { timeZones: JSON.stringify(sortedTimeZones) };

			// console.log('Saving time zones:', timeZones);
			// console.log('URL:', url);
			// console.log('Params:', params);

			const result = await postDataWithFetch(url, params);
			console.log('Save result:', result);

			// Handle response wrapped in data property
			const responseData = result?.data || result;
			
			if (responseData && responseData.status === true) {
				setTimeZones(sortedTimeZones); // Update display with sorted order
				const message = responseData.message || 'Time zones updated successfully';
				const count = responseData.count || sortedTimeZones.length;
				setStatusMessage(`${message}\n${count} time zone${count !== 1 ? 's' : ''} saved (sorted by UTC offset)`);
			} else {
				setErrorMessage(responseData?.message || `Failed to save time zones`);
			}
		} catch (error) {
			console.error('Error saving time zones:', error);
			setErrorMessage(`Error: ${error.message}`);
		} finally {
			setLoading(false);
		}
	};

	const handleLabelChange = (index, newLabel) => {
		const updatedTimeZones = [...timeZones];
		updatedTimeZones[index].label = newLabel;
		setTimeZones(updatedTimeZones);
	};

	const handleToggleTempPage = (index) => {
		const updatedTimeZones = timeZones.map((tz, i) => ({
			...tz,
			showOnTempPage: i === index ? !tz.showOnTempPage : false
		}));
		setTimeZones(updatedTimeZones);
		setStatusMessage('Toggle changed. Click SAVE to apply changes.');
	};

	return (
		<>
			<h3 className="admin-title-tag all-links" onClick={toggleTimeZones}>
				Configure Time Zones (tap to expand/collapse)
			</h3>
			<div style={{ display: showTimeZones ? 'block' : 'none' }}>
				<div className="form-row" style={{ marginBottom: '20px' }}>
					<div className="form-group">
						<input
							type="text"
							value={newCityName}
							onChange={(e) => setNewCityName(e.target.value)}
							onKeyPress={(e) => e.key === 'Enter' && handleAddCity()}
							placeholder="Enter city name (e.g., London, Tokyo, Sydney)"
							disabled={loading}
							style={{ marginBottom: '10px' }}
						/>
						<button
							className="my-button"
							style={{ background: "#0077FF", color: "#fff", marginRight: '10px' }}
							onClick={handleAddCity}
							disabled={loading}
						>
							{loading ? 'LOOKING UP...' : 'ADD CITY'}
						</button>
					</div>
				</div>

				{errorMessage && (
					<div style={{ color: 'red', marginBottom: '10px', padding: '10px', background: '#ffe6e6', borderRadius: '4px' }}>
						{errorMessage}
					</div>
				)}

				<div style={{ marginTop: '20px' }}>
					<p style={{ fontSize: '14px', color: '#666', marginBottom: '10px' }}>
						Toggle "Show on Temp Page" to display one timezone in the upper left on the site closed page (only one can be enabled at a time)
					</p>
					{timeZones.map((tz, index) => (
						<div key={index} className="form-row" style={{ display: 'flex', alignItems: 'center', marginBottom: '10px' }}>
							<div className="form-group">
								<input
									type="text"
									value={tz.label}
									onChange={(e) => handleLabelChange(index, e.target.value)}
									placeholder="City Label"
									disabled={index === 0}
								/>
							</div>
							<div className="form-group">
								<input
									type="text"
									value={tz.timeZone}
									readOnly
									placeholder="Time Zone"
									style={{ background: '#f5f5f5', cursor: 'not-allowed' }}
								/>
							</div>
							<label className="checkbox-container" style={{ marginLeft: '10px', marginRight: '10px' }}>
								<input
									className="sr-only"
									type="checkbox"
									checked={tz.showOnTempPage || false}
									onChange={() => handleToggleTempPage(index)}
								/>
								<div className="slider"></div>
								<span className="label" style={{ fontSize: '12px', whiteSpace: 'nowrap' }}>Show on Temp Page</span>
							</label>
							{index !== 0 && (
								<button
									onClick={() => handleRemoveCity(index)}
									style={{
										width: '32px',
										height: '32px',
										borderRadius: '50%',
										background: '#FF4444',
										color: '#fff',
										border: 'none',
										cursor: 'pointer',
										fontSize: '18px',
										fontWeight: 'bold',
										display: 'flex',
										alignItems: 'center',
										justifyContent: 'center',
										marginLeft: '10px',
										flexShrink: 0
									}}
								>
									×
								</button>
							)}
						</div>
					))}
				</div>

				<div className="btn-container" style={{ marginTop: '20px' }}>
					<button
						className="my-button"
						style={{ background: "#33B5E5", color: "#fff" }}
						onClick={handleSaveTimeZones}
						disabled={loading}
					>
						{loading ? 'SAVING...' : 'SAVE TIME ZONES'}
					</button>
				</div>

				{statusMessage && (
					<div style={{ color: 'green', marginTop: '10px', padding: '10px', background: '#e6ffe6', borderRadius: '4px', whiteSpace: 'pre-line' }}>
						{statusMessage}
					</div>
				)}
			</div>
		</>
	);
};

export default TimeZoneConfig;
